import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useEvaluation } from "../../api/queries/performanceQueries";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatDateTime, formatPlainDate } from "../../utils/dateTime";
import { employeeName } from "../../utils/text";
import EvaluationForm from "./components/EvaluationForm";
import { statusLabel } from "./components/evaluationUtils";
import "./performance.css";

/**
 * Paper-form layout for printing (no API export yet). Opens the browser's
 * print dialog once loaded. Signature lines: the evaluator and the employee
 * (agreed / contested).
 */
const EvaluationPrintPage = () => {
  const { id } = useParams();
  const query = useEvaluation(id);
  const ev = query.data;
  const printed = useRef(false);

  useEffect(() => {
    if (ev && !printed.current) {
      printed.current = true;
      document.title = `${employeeName(ev.employee)} – ${ev.template?.name || "Evaluation"}`;
      setTimeout(() => window.print(), 300);
    }
  }, [ev]);

  if (query.isLoading)
    return (
      <div className="text-center py-5">
        <span
          className="spinner-border text-primary"
          role="status"
          aria-label="Loading"
        ></span>
      </div>
    );
  if (query.isError || !ev)
    return (
      <div className="alert alert-danger m-4">
        {getApiErrorMessage(query.error, "Could not load this evaluation.")}
      </div>
    );

  return (
    <div className="print-page">
      <div className="print-toolbar no-print">
        <button
          type="button"
          className="btn btn-primary profile-btn"
          onClick={() => window.print()}
        >
          <i className="bi bi-printer"></i> Print
        </button>
        <button
          type="button"
          className="btn profile-btn profile-btn-white"
          onClick={() => window.close()}
        >
          Close
        </button>
      </div>

      <header className="print-header">
        <h1>{ev.template?.name}</h1>
        <div className="text-slate-600 fs-085">
          {ev.template?.code} v{ev.template?.version} · {statusLabel(ev)}
        </div>
      </header>

      <table className="print-info">
        <tbody>
          <tr>
            <th>Employee</th>
            <td>
              {employeeName(ev.employee)} ({ev.employee?.employeeId})
            </td>
            <th>Branch</th>
            <td>{ev.employee?.branch?.branchName || "—"}</td>
          </tr>
          <tr>
            <th>Period</th>
            <td>
              {ev.periodLabel} ({formatPlainDate(ev.periodStart)} –{" "}
              {formatPlainDate(ev.periodEnd)})
            </td>
            <th>Evaluator</th>
            <td>{employeeName(ev.evaluator)}</td>
          </tr>
          <tr>
            <th>Total score</th>
            <td>
              <strong>{ev.scores?.total ?? ev.totalScore ?? "—"}</strong> / 100
            </td>
            <th>Rating</th>
            <td>
              <strong>{ev.scores?.band || ev.ratingBand || "—"}</strong>
            </td>
          </tr>
        </tbody>
      </table>

      {ev.scores?.sections?.length > 0 && (
        <table className="print-info print-scores">
          <thead>
            <tr>
              <th>Section</th>
              <th>Weight</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {ev.scores.sections.map((s) => (
              <tr key={s.key}>
                <td>{s.title}</td>
                <td>{s.weight}%</td>
                <td>{s.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <EvaluationForm
        sections={ev.sections}
        responses={ev.responses}
        scores={ev.scores}
      />

      <section className="print-signatures">
        <div className="print-signature">
          <div className="print-signature-name">
            {employeeName(ev.evaluator)}
          </div>
          <div className="print-signature-line">
            Evaluator
            {ev.submittedAt ? ` · ${formatDateTime(ev.submittedAt)}` : ""}
          </div>
        </div>
        <div className="print-signature">
          <div className="print-signature-name">
            {employeeName(ev.employee)}
          </div>
          <div className="print-signature-line">
            Employee
            {ev.employeeResponse
              ? ` · ${ev.employeeResponse === "CONTEST" ? "Contested" : "Agreed"}`
              : ""}
            {ev.acknowledgedAt ? ` · ${formatDateTime(ev.acknowledgedAt)}` : ""}
          </div>
          {ev.employeeComment && (
            <div className="fs-075 mt-1">Comment: {ev.employeeComment}</div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EvaluationPrintPage;
